/**
 * Cookie utility functions for managing authentication tokens
 * Supports subdomain configuration
 * // TODO: Update cookieStore when all browsers support it
 * https://developer.mozilla.org/en-US/docs/Web/API/CookieStore
 */

export interface CookieOptions {
  expires?: number | Date;
  path?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
}

/**
 * Set a cookie
 */
export const setCookie = (
  name: string,
  value: string,
  options: CookieOptions = {},
): void => {
  if (typeof document === 'undefined') return;

  const defaultOptions: CookieOptions = {
    path: '/',
    // expires: 365, // days  --> Remove for use session cookie
  };
  const finalOptions = { ...defaultOptions, ...options };

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (finalOptions.expires) {
    let expires: Date;
    if (typeof finalOptions.expires === 'number') {
      expires = new Date();
      expires.setTime(
        expires.getTime() + finalOptions.expires * 24 * 60 * 60 * 1000,
      );
    } else {
      // eslint-disable-next-line prefer-destructuring
      expires = finalOptions.expires;
    }
    cookieString += `; expires=${expires.toUTCString()}`;
  }

  if (finalOptions.path) {
    cookieString += `; path=${finalOptions.path}`;
  }

  if (finalOptions.secure) {
    cookieString += '; secure';
  }

  if (finalOptions.sameSite) {
    cookieString += `; samesite=${finalOptions.sameSite}`;
  }

  document.cookie = cookieString;
};

/**
 * Get a cookie value by name
 */
export const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;

  const nameEQ = `${encodeURIComponent(name)}=`;
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i += 1) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
    }
  }

  return null;
};

/**
 * Remove a cookie
 */
export const removeCookie = (name: string): void => {
  if (typeof document === 'undefined') return;

  // Set cookie with past expiration date to remove it
  setCookie(name, '', {
    expires: new Date(0),
    path: '/',
  });
};

/**
 * Check if cookies are available
 */
export const isCookieAvailable = (): boolean => {
  if (typeof document === 'undefined') return false;

  try {
    const testKey = '__cookie_test__';
    setCookie(testKey, 'test', { expires: 1 });
    const exists = getCookie(testKey) === 'test';
    removeCookie(testKey);
    return exists;
  } catch {
    return false;
  }
};

/**
 * Get all cookies as an object
 */
export const getAllCookies = (): Record<string, string> => {
  if (typeof document === 'undefined') return {};

  const cookies: Record<string, string> = {};
  const cookieArray = document.cookie.split(';');

  cookieArray.forEach((cookie) => {
    const [name, value] = cookie.split('=').map((c) => c.trim());
    if (name && value) {
      cookies[decodeURIComponent(name)] = decodeURIComponent(value);
    }
  });

  return cookies;
};
